/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import isEmail from 'validator/lib/isEmail';
import {
  Checkbox,
  FormControlLabel,
  TableCell,
  TableRow,
  TextField
} from '@mui/material';
import {
  CheckRounded,
  Create as CreateIcon,
  Close as CloseIcon,
  FileCopy,
  WhatsApp as WhatsAppIcon,
  AccountCircle
} from '@mui/icons-material';

// COMPONENTS
import KogCheckBoxControlled from '@/main/components/shared/checkbox/checkBoxControlled';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import BoxGrid from '@/main/components/shared/boxGrid/boxGrid';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import KogAutoCompleteControlled from '../inputAutoComplete/autoCompleteControlled';

// SERVICES
import { contactApi, countryInfoApi } from '@/main/api';
import { KeyValue } from '@/main/models/common';
import {
  GetPhoneCode,
  getPhoneCountryCode,
  getTranslateByKey,
  isNullUndefined
} from '@/main/services/utils';

// REDUX
import { getContactType } from '@/main/store/contactType';
import { useContactTypeReducer } from '@/main/store/contactType/contactTypeHooks';

import * as SP from '../styles';
import * as S from './styles';
import { ContactResponse } from '@/main/models/response';
import { Guid } from 'guid-typescript';
import { ContactSaveRequest, ContactTypeDomain } from '@/main/models/request';
import { ContactTypeEnum } from '@/main/services/enum/ContactTypeEnum';

export interface IContactInfoState {
  id: string;
  contactType: KeyValue;
  firstName: string;
  lastName: string;
  phoneNr: string;
  extension: string;
  cellphoneNr: string;
  email: string;
  isPrimaryContact: boolean;
  phoneIsWhatsApp: boolean;
  cellphoneIsWhatsApp: boolean;
  codePhoneNumber: KeyValue | null;
  codeCellPhoneNumber: KeyValue | null;
}
interface IContactInfoProps {
  setDataContacts: (
    cb: (data: IContactInfoState[]) => IContactInfoState[]
  ) => void;
  dataContacts: IContactInfoState[];
  typeCompany?: string;
  watcherCompany?: KeyValue | null;
  watcherCountry?: KeyValue | null;
  companyType?: number;
  companyName?: string;
}
interface IContactInfoForm {
  contactType: KeyValue | null;
  firstName: string;
  lastName: string;
  phoneNr: string;
  extension: string;
  cellphoneNr: string;
  email: string;
  isPrimaryContact: boolean;
  phoneIsWhatsApp: boolean;
  cellphoneIsWhatsApp: boolean;
  id: string | null;
  codePhoneNumber: KeyValue | null;
  codeCellPhoneNumber: KeyValue | null;
}

const INITIAL_FORM_STATE: IContactInfoForm = {
  contactType: null as KeyValue | null,
  firstName: '',
  lastName: '',
  phoneNr: '',
  extension: '',
  cellphoneNr: '',
  email: '',
  isPrimaryContact: false,
  phoneIsWhatsApp: false,
  cellphoneIsWhatsApp: false,
  id: null,
  codePhoneNumber: null as KeyValue | null,
  codeCellPhoneNumber: null as KeyValue | null
};

function ContactInfo({
  dataContacts,
  setDataContacts,
  typeCompany = '',
  watcherCompany = null,
  watcherCountry = null,
  companyType,
  companyName = ''
}: IContactInfoProps) {
  const {
    control,
    handleSubmit,
    formState: { errors },
    clearErrors,
    setError,
    setValue,
    reset
  } = useForm<IContactInfoForm>({
    mode: 'all',
    defaultValues: INITIAL_FORM_STATE
  });
  // Reducers
  const dispatch = useDispatch();
  const contactTypeReducer = useContactTypeReducer();

  // States
  const [hasEdit, setHasEdit] = useState(false);

  // Handle Functions
  function applyContactDefaults(data: IContactInfoForm): IContactInfoForm {
    const otherContactType =
      contactTypeReducer.contactType.data?.find(
        item => String(item.key) === String(ContactTypeEnum.Other)
      ) ?? null;

    return {
      ...data,
      firstName: data.firstName?.trim() ? data.firstName : companyName,
      contactType: data.contactType ?? otherContactType
    };
  }

  function handleSubmitContactInfo(data: IContactInfoForm) {
    setDataContacts(prevState => {
      const havePrimaryContact = prevState.some(item => item.isPrimaryContact);
      if (havePrimaryContact && data.isPrimaryContact) {
        setError('isPrimaryContact', {
          type: 'unique',
          message: getTranslateByKey(
            'information.onlyOnePrimaryContactIsAllowed',
            true
          )
        });
        return prevState;
      }

      const newData = {
        ...applyContactDefaults(data),

        id: uuidv4()
      } as IContactInfoState;
      reset(INITIAL_FORM_STATE);
      loadInfoCountry();
      return [...prevState, newData];
    });
  }
  function handleEditContactInfo(contact: IContactInfoState) {
    setHasEdit(true);
    setValue('contactType', contact.contactType);
    setValue('firstName', contact.firstName);
    setValue('lastName', contact.lastName);
    setValue('phoneNr', contact.phoneNr);
    setValue('extension', contact.extension);
    setValue('cellphoneNr', contact.cellphoneNr);
    setValue('email', contact.email);
    setValue('isPrimaryContact', contact.isPrimaryContact);
    setValue('phoneIsWhatsApp', contact.phoneIsWhatsApp);
    setValue('cellphoneIsWhatsApp', contact.cellphoneIsWhatsApp);
    setValue('id', contact.id);
    setValue('codePhoneNumber', contact.codePhoneNumber);
    setValue('codeCellPhoneNumber', contact.codeCellPhoneNumber);
    clearErrors();
  }
  function handleSubmitEditContactInfo(data: IContactInfoForm) {
    setDataContacts(prevState => {
      const clonePrevState = JSON.parse(
        JSON.stringify(prevState)
      ) as IContactInfoState[];
      const havePrimaryContact = clonePrevState.find(
        item => item.isPrimaryContact
      );

      const hasSameContact = havePrimaryContact?.id === data.id;

      if (havePrimaryContact && data.isPrimaryContact && !hasSameContact) {
        setError('isPrimaryContact', {
          type: 'unique',
          message: getTranslateByKey('information.onlyOneContact', true)
        });
        return clonePrevState;
      }

      const index = clonePrevState.findIndex(item => item.id === data.id);
      clonePrevState[index] = applyContactDefaults(data) as IContactInfoState;

      reset();
      loadInfoCountry();
      setHasEdit(false);

      return [...clonePrevState];
    });
  }
  function handleDeleteContactInfo(id: string) {
    setHasEdit(false);
    setDataContacts(prevState => {
      const index = prevState.findIndex(item => item.id === id);
      prevState.splice(index, 1);
      return [...prevState];
    });
  }

  // Async Functions
  function handleCopyContacts() {
    contactApi
      .getContactBodyshop({ guidBodyShop: String(watcherCompany?.key) })
      .then(res => {
        const responseContacts = res.data.result;

        if (responseContacts.length === 0) {
          toast.error(
            getTranslateByKey('headquarterNotHaveContacts').replace(
              '{0}',
              String(watcherCompany?.value)
            )
          );
          return;
        }

        const formattedContacts = responseContacts.map(contact =>
          formattedContactGet(contact)
        );
        setDataContacts(() => formattedContacts);
      });
  }

  function loadInfoCountry() {
    if (isNullUndefined(watcherCountry)) return;
    countryInfoApi.getCountryInfo(String(watcherCountry.key)).then(response => {
      const {
        data: { result }
      } = response;
      const codeCountry = getPhoneCountryCode();
      const findCodePhoneNumber = codeCountry.find(
        item => item.key === result.dialingCode
      ) as KeyValue;
      setValue('codePhoneNumber', findCodePhoneNumber);
      setValue('codeCellPhoneNumber', findCodePhoneNumber);
    });
  }

  useEffect(() => {
    const request: ContactTypeDomain = {
      companyType: companyType
    };
    dispatch(getContactType(request));
  }, []);

  useEffect(() => {
    loadInfoCountry();
  }, [watcherCountry?.key]);

  // Constants for Layout
  const hasSelectedHeadquarter = watcherCompany !== null;

  return (
    <S.Container id="id-register-contactInfo">
      <form
        onSubmit={handleSubmit(
          hasEdit ? handleSubmitEditContactInfo : handleSubmitContactInfo
        )}
        noValidate>
        <SP.HeaderSection>
          <SP.HeaderText>
            {getTranslateByKey('information.contactInfo')}
          </SP.HeaderText>
          {typeCompany === 'branch' && (
            <SecondaryButton
              type="button"
              disabled={!hasSelectedHeadquarter || dataContacts.length > 0}
              onClick={handleCopyContacts}
              style={{ padding: '.2rem .8rem', margin: '0 0 0 1rem' }}
              id="id-register-txt-copyContacts">
              <FileCopy fontSize="small" />
              <span style={{ lineHeight: '0.7rem', marginLeft: '.4rem' }}>
                {getTranslateByKey('information.copyContactsFromHeadquarters')}
              </span>
            </SecondaryButton>
          )}
        </SP.HeaderSection>
        <S.AreaForm>
          <KogAutoCompleteControlled
            label={getTranslateByKey('information.contactType')}
            nameCustom="contactType"
            control={control}
            options={contactTypeReducer.contactType.data}
            isLoading={contactTypeReducer.contactType.isLoading}
            rulesCustom={{ required: false }}
            hasErrorCustom={false}
            errorMessageCustom=""
            onChangeCustom={() => { }}
            id="id-register-txt-contactType"
          />
          <KogInputTextControlledNew
            name="firstName"
            label={getTranslateByKey('information.firstName')}
            control={control}
            controllerProps={{ rules: { maxLength: 100 } }}
            id="id-register-txt-firstName"
          />
          <KogInputTextControlledNew
            name="lastName"
            label={getTranslateByKey('information.lastName')}
            control={control}
            controllerProps={{ rules: { maxLength: 100 } }}
            id="id-register-txt-lastName"
          />
          <S.AreaInputNumber>
            <KogInputTextControlledNew
              hasOnlyNumber
              name="phoneNr"
              label={getTranslateByKey('information.phoneNr')}
              control={control}
              inputProps={{ maxLength: 12 }}
              controllerProps={{
                rules: {
                  required: true,
                  maxLength: 12,
                  minLength: 10
                }
              }}
              errorMessageCustom={
                errors.phoneNr?.type === 'required'
                  ? getTranslateByKey('information.phoneNrRequired', true)
                  : ''
              }
              id="id-register-txt-phoneNr"
              InputProps={{
                startAdornment: (
                  <KogAutoCompleteControlled
                    label={''}
                    nameCustom="codePhoneNumber"
                    control={control}
                    options={getPhoneCountryCode()}
                    isLoading={false}
                    hasErrorCustom={!!errors.codePhoneNumber}
                    onChangeCustom={() => { }}
                    id="id-register-txt-codePhoneNumber"
                    disableClearable={true}
                  />
                )
              }}
            />
            <Controller
              name="phoneIsWhatsApp"
              control={control}
              render={({ field: { onChange, value } }) => (
                <S.AreaCheckbox>
                  <FormControlLabel
                    label={getTranslateByKey('information.whatsappNumber')}
                    control={
                      <Checkbox
                        color="primary"
                        checked={value}
                        onChange={e => {
                          onChange(e.target.checked);
                        }}
                        id="id-register-checkBox-phoneIsWhatsApp"
                      />
                    }
                  />
                </S.AreaCheckbox>
              )}
            />
          </S.AreaInputNumber>
          <KogInputTextControlledNew
            name="extension"
            label={getTranslateByKey('information.extension')}
            control={control}
            inputProps={{ maxLength: 100 }}
            controllerProps={{ rules: { maxLength: 100 } }}
            id="id-register-txt-extension"
          />
          <S.AreaInputNumber>
            <KogInputTextControlledNew
              hasOnlyNumber
              name="cellphoneNr"
              label={getTranslateByKey('information.cellphoneNr')}
              control={control}
              inputProps={{ maxLength: 12 }}
              controllerProps={{ rules: { maxLength: 12, minLength: 10 } }}
              id="id-register-txt-cellphoneNr"
              InputProps={{
                startAdornment: (
                  <KogAutoCompleteControlled
                    label={''}
                    nameCustom="codeCellPhoneNumber"
                    control={control}
                    options={getPhoneCountryCode()}
                    isLoading={false}
                    hasErrorCustom={!!errors.codeCellPhoneNumber}
                    onChangeCustom={() => { }}
                    id="id-register-txt-codeCellPhoneNumber"
                    disableClearable={true}
                  />
                )
              }}
            />
            <Controller
              name="cellphoneIsWhatsApp"
              control={control}
              render={({ field: { onChange, value } }) => (
                <S.AreaCheckbox>
                  <FormControlLabel
                    label={getTranslateByKey('information.whatsappNumber')}
                    control={
                      <Checkbox
                        color="primary"
                        checked={value}
                        onChange={e => {
                          onChange(e.target.checked);
                        }}
                        id="id-register-checkBox-cellphoneIsWhatsApp"
                      />
                    }
                  />
                </S.AreaCheckbox>
              )}
            />
          </S.AreaInputNumber>
          <KogInputTextControlledNew
            name="email"
            label={getTranslateByKey('information.email')}
            control={control}
            inputProps={{ maxLength: 200 }}
            controllerProps={{
              rules: {
                required: true,
                maxLength: 200,
                validate: {
                  isEmail: value =>
                    isEmail(value) ||
                    getTranslateByKey('information.invalidEmail', true)
                }
              }
            }}
            errorMessageCustom={
              errors.email?.type === 'required'
                ? getTranslateByKey('information.emailRequired', true)
                : ''
            }
            id="id-register-txt-email"
          />
          <div
            style={{
              height: 'fit-content',
              display: 'flex',
              alignItems: 'flex-start',
              marginTop: '.4rem'
            }}>
            <KogCheckBoxControlled
              nameCustom="isPrimaryContact"
              label={getTranslateByKey('information.isPrimaryContact')}
              control={control}
              hasErrorCustom={!!errors.isPrimaryContact}
              errorMessageCustom={
                errors.isPrimaryContact?.type === 'unique'
                  ? errors.isPrimaryContact?.message
                  : ''
              }
              id="id-register-checkBox-primaryContact"
            />
            <SecondaryButton
              id="id-register-btn-addContact"
              type="button"
              onClick={e =>
                handleSubmit(
                  hasEdit
                    ? handleSubmitEditContactInfo
                    : handleSubmitContactInfo
                )()
              }
              style={{ padding: '.2rem .8rem', margin: '.5rem 0 0 .8rem' }}>
              {hasEdit
                ? getTranslateByKey('information.edit')
                : getTranslateByKey('information.add')}
              {` ${getTranslateByKey('information.contact').toLowerCase()}`}
            </SecondaryButton>
          </div>
        </S.AreaForm>
      </form>
      {dataContacts.length > 0 && (
        <BoxGrid
          rowHeaders={[
            getTranslateByKey('information.contactType'),
            getTranslateByKey('information.contactName'),
            getTranslateByKey('information.phoneNr'),
            getTranslateByKey('information.extension'),
            getTranslateByKey('information.cellphoneNr'),
            getTranslateByKey('information.email'),
            getTranslateByKey('information.primary'),
            getTranslateByKey('information.actions')
          ]}>
          {dataContacts.map(contact => (
            <TableRow key={contact.cellphoneNr}>
              <TableCell>{contact.contactType?.value}</TableCell>
              <TableCell>
                {contact.firstName} {contact.lastName}
              </TableCell>
              <TableCell>
                <S.CollumnPhoneNumber>
                  {contact.codePhoneNumber &&
                    contact.phoneNr &&
                    `${contact.codePhoneNumber.value} ${contact.phoneNr}`}
                  {contact.phoneIsWhatsApp && (
                    <>
                      &nbsp;
                      <S.CustomColumn
                        title={getTranslateByKey('information.whatsappMsg')}>
                        <WhatsAppIcon
                          style={{ color: '#25D366' }}
                          fontSize="small"
                        />
                      </S.CustomColumn>
                    </>
                  )}
                </S.CollumnPhoneNumber>
              </TableCell>
              <TableCell>{contact.extension}</TableCell>
              <TableCell>
                <S.CollumnPhoneNumber>
                  {contact.codeCellPhoneNumber &&
                    contact.cellphoneNr &&
                    `${contact.codeCellPhoneNumber.value} ${contact.cellphoneNr}`}
                  {contact.cellphoneIsWhatsApp &&
                    contact.codeCellPhoneNumber &&
                    contact.cellphoneNr && (
                      <>
                        &nbsp;
                        <S.CustomColumn
                          title={getTranslateByKey('information.whatsappMsg')}>
                          <WhatsAppIcon
                            style={{ color: '#25D366' }}
                            fontSize="small"
                          />
                        </S.CustomColumn>
                      </>
                    )}
                </S.CollumnPhoneNumber>
              </TableCell>
              <TableCell>{contact.email}</TableCell>
              <TableCell>
                {contact.isPrimaryContact && <CheckRounded color="primary" />}
              </TableCell>
              <TableCell width="5%">
                <CreateIcon
                  onClick={() => handleEditContactInfo(contact)}
                  id={
                    'id-register-icon-editContact-' +
                    (contact.firstName + contact.lastName)
                  }
                  className="cursor-pointer"
                  color="primary"
                />
                <CloseIcon
                  onClick={() => handleDeleteContactInfo(contact.id)}
                  id={
                    'id-register-icon-deleteContact-' +
                    (contact.firstName + contact.lastName)
                  }
                  className="cursor-pointer"
                  color="primary"
                />
              </TableCell>
            </TableRow>
          ))}
        </BoxGrid>
      )}
    </S.Container>
  );
}

export function formattedContactGet(
  contact: ContactResponse
): IContactInfoState {
  const formatedCellPhone = GetPhoneCode(contact.cellphone);
  const formatedPhone = GetPhoneCode(contact.phone);
  const codeCountry = getPhoneCountryCode();
  const findCodeCellPhoneNumber = codeCountry.find(
    item => item.key === formatedCellPhone.codePhone
  ) ?? { key: '', value: '' };
  const findCodePhoneNumber = codeCountry.find(
    item => item.key === formatedPhone.codePhone
  ) ?? { key: '', value: '' };

  const contactFormatted: IContactInfoState = {
    // id: uuidv4(),
    id: contact.guid,
    cellphoneNr: formatedCellPhone.number,
    contactType: {
      key: contact.contactType.idContactType,
      value: contact.contactType.name
    },
    email: contact.email,
    firstName: contact.firstName,
    isPrimaryContact: contact.principal,
    lastName: contact.lastName,
    phoneNr: formatedPhone.number,
    cellphoneIsWhatsApp: contact.cellphoneIsWhatsApp,
    phoneIsWhatsApp: contact.phoneIsWhatsApp,
    codeCellPhoneNumber: findCodeCellPhoneNumber,
    codePhoneNumber: findCodePhoneNumber,
    extension: contact.extension
  };
  return contactFormatted;
}

export function formattedContactSave(
  contacts: IContactInfoState[],
  isEditContact = false
): ContactSaveRequest[] {
  const formattedContact = (
    contact: IContactInfoState
  ): ContactSaveRequest => ({
    ...(isEditContact && {
      guid: Guid.isGuid(contact.id) ? contact.id : Guid.EMPTY
    }),
    firstName: contact.firstName,
    lastName: contact.lastName,
    email: contact.email,
    phone: `${contact.codePhoneNumber?.key} ${contact.phoneNr}`,
    extension: contact.extension,
    cellphone: `${contact.codeCellPhoneNumber?.key} ${contact.cellphoneNr}`,
    principal: contact.isPrimaryContact,
    phoneIsWhatsApp: contact.phoneIsWhatsApp,
    cellphoneIsWhatsApp: contact.cellphoneIsWhatsApp,
    idContactType: contact.contactType ? String(contact.contactType.key) : '',
    contactType: {
      idContactType: contact.contactType ? Number(contact.contactType.key) : 0,
      name: contact.contactType?.value ?? ''
    }
  });

  return contacts.map(contact => formattedContact(contact));
}

export default ContactInfo;
